import React from 'react';
import { Box, Grid, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  TrendingUp as IngresosIcon,
  TrendingDown as EgresosIcon,
  WarningAmber as WarningIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { FlujoFondosKpis } from '../../../services/tesoreriaFlujoFondosService';

interface Props {
  kpis: FlujoFondosKpis | null;
  loading?: boolean;
}

/** Totales de KPI sin decimales para que no desborden en cards angostas. */
const formatCurrencyEntero = (amount: number): string =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(amount || 0));

const KpiCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  color: string;
  icon: React.ReactNode;
  alert?: boolean;
}> = ({ title, value, subtitle, color, icon, alert }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.75,
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: alert ? '#ef5350' : '#e0e0e0',
      bgcolor: alert ? 'rgba(239,83,80,0.04)' : '#fff',
    }}
  >
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: `${color}14`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Stack>
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: alert ? '#c62828' : '#1a1a1a',
            lineHeight: 1.25,
            fontSize: { xs: '1.05rem', md: '1.2rem' },
          }}
        >
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  </Paper>
);

const FlujoFondosKpisBar: React.FC<Props> = ({ kpis }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!kpis) return null;

  const fmtDate = (d?: string | null) => {
    if (!d) return undefined;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return undefined;
    return dt.toLocaleDateString('es-AR');
  };

  return (
    <Grid container spacing={isMobile ? 1.5 : 2}>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Saldo inicial"
          value={formatCurrencyEntero(kpis.saldoInicial)}
          color="#1976d2"
          icon={<WalletIcon fontSize="small" />}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Ingresos"
          value={formatCurrencyEntero(kpis.totalIngresos)}
          color="#2e7d32"
          icon={<IngresosIcon fontSize="small" />}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Egresos"
          value={formatCurrencyEntero(kpis.totalEgresos)}
          color="#ed6c02"
          icon={<EgresosIcon fontSize="small" />}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Saldo final"
          value={formatCurrencyEntero(kpis.saldoFinal)}
          color={kpis.saldoFinal < 0 ? '#c62828' : '#1976d2'}
          icon={<ChartIcon fontSize="small" />}
          alert={kpis.saldoFinal < 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Peor saldo"
          value={formatCurrencyEntero(kpis.peorSaldo)}
          subtitle={fmtDate(kpis.peorSaldoFecha)}
          color="#c62828"
          icon={<WarningIcon fontSize="small" />}
          alert={kpis.peorSaldo < 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiCard
          title="Períodos en negativo"
          value={String(kpis.periodosNegativos)}
          subtitle={
            kpis.umbralExcedente > 0
              ? `Pico ${formatCurrencyEntero(kpis.picoExcedente)}`
              : undefined
          }
          color="#c62828"
          icon={<WarningIcon fontSize="small" />}
          alert={kpis.periodosNegativos > 0}
        />
      </Grid>
    </Grid>
  );
};

export default FlujoFondosKpisBar;
